import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { hoyISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function SolicitudCambioZonaDialog({ open, onOpenChange, asignacionInicial, onSuccess }) {
  const [zonas, setZonas] = useState([]);
  const [tramosCompaneros, setTramosCompaneros] = useState([]);
  const [form, setForm] = useState({
    tipo: "intercambio",
    asignacion_id: "",
    fecha_inicio: hoyISO(),
    fecha_fin: hoyISO(),
    hora_inicio: "",
    hora_fin: "",
    zona_destino_id: "",
    asignacion_intercambio_id: "",
    motivo: "",
  });
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/zonas").then(({ data }) => setZonas(data)).catch(() => {});
    if (asignacionInicial) {
      setForm((f) => ({
        ...f,
        tipo: "intercambio",
        asignacion_id: String(asignacionInicial.id),
        fecha_inicio: asignacionInicial.fecha,
        fecha_fin: asignacionInicial.fecha,
        hora_inicio: asignacionInicial.hora_inicio,
        hora_fin: asignacionInicial.hora_fin,
      }));
    }
  }, [open, asignacionInicial]);

  useEffect(() => {
    if (!open || !form.fecha_inicio) return;
    api.get("/zonas/tramos-companeros", {
      params: { fecha: form.fecha_inicio, fecha_fin: form.fecha_fin || form.fecha_inicio },
    })
      .then(({ data }) => setTramosCompaneros(data))
      .catch(() => setTramosCompaneros([]));
  }, [open, form.fecha_inicio, form.fecha_fin]);

  const enviar = async () => {
    if (!form.motivo.trim() || form.motivo.trim().length < 3) {
      toast.error("Indica el motivo del cambio");
      return;
    }
    if (form.tipo === "cambio" && !form.zona_destino_id && !form.asignacion_id) {
      toast.error("Indica la zona destino");
      return;
    }
    if (form.tipo === "intercambio" && !form.asignacion_intercambio_id) {
      toast.error("Selecciona el tramo del compañero");
      return;
    }
    setEnviando(true);
    try {
      const payload = {
        tipo: form.tipo,
        fecha_inicio: form.fecha_inicio,
        fecha_fin: form.fecha_fin,
        motivo: form.motivo.trim(),
      };
      if (form.asignacion_id) payload.asignacion_id = Number(form.asignacion_id);
      if (form.hora_inicio) payload.hora_inicio = form.hora_inicio;
      if (form.hora_fin) payload.hora_fin = form.hora_fin;
      if (form.zona_destino_id) payload.zona_destino_id = Number(form.zona_destino_id);
      if (form.asignacion_intercambio_id) payload.asignacion_intercambio_id = Number(form.asignacion_intercambio_id);
      await api.post("/zonas/solicitudes-cambio", payload);
      toast.success(
        form.tipo === "intercambio"
          ? "Solicitud enviada. Tu compañero y el encargado serán notificados."
          : "Solicitud enviada al encargado"
      );
      onSuccess?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="solicitud-cambio-zona-dialog">
        <DialogHeader>
          <DialogTitle>Solicitar cambio o intercambio de zona</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5">
            <Label>Tipo de solicitud</Label>
            <select className="h-9 w-full rounded-md border border-zinc-200 px-2" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
              <option value="intercambio">Intercambio con otro trabajador</option>
              <option value="cambio">Cambio de zona / horario</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>Desde</Label>
              <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Hasta</Label>
              <Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} />
            </div>
          </div>
          {form.tipo === "cambio" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label>Hora inicio (opc.)</Label>
                  <Input type="time" value={form.hora_inicio} onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Hora fin (opc.)</Label>
                  <Input type="time" value={form.hora_fin} onChange={(e) => setForm({ ...form, hora_fin: e.target.value })} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Zona destino</Label>
                <select className="h-9 w-full rounded-md border border-zinc-200 px-2" value={form.zona_destino_id} onChange={(e) => setForm({ ...form, zona_destino_id: e.target.value })}>
                  <option value="">Selecciona zona…</option>
                  {zonas.map((z) => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                </select>
              </div>
            </>
          )}
          {form.tipo === "intercambio" && (
            <div className="space-y-1.5">
              <Label>Tramo del compañero</Label>
              <select
                className="h-9 w-full rounded-md border border-zinc-200 px-2"
                value={form.asignacion_intercambio_id}
                onChange={(e) => setForm({ ...form, asignacion_intercambio_id: e.target.value })}
              >
                <option value="">Selecciona tramo…</option>
                {tramosCompaneros.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.usuario} — {c.zona_nombre} {c.hora_inicio}–{c.hora_fin} ({c.fecha})
                  </option>
                ))}
              </select>
              <p className="text-xs text-zinc-500">
                Primero deberá aceptar tu compañero; después el encargado revisará el cambio.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <Textarea value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} rows={3} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={enviar} disabled={enviando} className="bg-violet-600 hover:bg-violet-700">
            {enviando ? "Enviando…" : "Enviar solicitud"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
